import React, { useEffect, useRef, useState } from "react";
import { Files, Users } from "../lib/api";
import { Button } from "./ui/button";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "./ui/tooltip";
import { toast } from "sonner";
import { Upload, Trash2, HelpCircle, FileImage, Plus, Camera, Download } from "lucide-react";
import { cn } from "../lib/utils";
import { formatAuditDate, formatAuditTime } from "../lib/auditFormat";
import { getUploadPolicy, validateUpload, downloadUrlFor } from "../lib/uploadPolicy";

const TYPE_META = {
  aadhar: { label: "Aadhar Card", help: "Upload best quality photo or e-Aadhar card preferable.", multiple: false, required: true },
  passport_photo: { label: "Passport Size Photo", help: "Recent passport-size photo of the customer.", multiple: false, required: true },
  pan: { label: "PAN Card", help: "Upload a clear photo of the PAN card.", multiple: false, required: false },
  vin_photo: { label: "Chassis Number Photo", help: "Photo of the chassis VIN plate. Can be uploaded by Sales or PDI.", multiple: false, required: false },
  pdi_check_sheet: { label: "PDI Check Sheet", help: "Filled and signed PDI check sheet (PDF or photo).", multiple: false, required: false },
  vehicle_photo_front: { label: "Vehicle · Front View", help: "Front view of the vehicle.", multiple: false, required: false },
  vehicle_photo_left: { label: "Vehicle · Left View", help: "Left side view.", multiple: false, required: false },
  vehicle_photo_right: { label: "Vehicle · Right View", help: "Right side view.", multiple: false, required: false },
  vehicle_photo_rear: { label: "Vehicle · Rear View", help: "Rear view of the vehicle.", multiple: false, required: false },
  tax_invoice: { label: "Tax Invoice", help: "Generated tax invoice PDF / scan.", multiple: false, required: false },
  tax_receipt: { label: "Road Tax Receipt", help: "Receipt acknowledging road tax payment.", multiple: false, required: false },
  hsrp_receipt: { label: "HSRP Receipt", help: "HSRP allocation/order acknowledgement.", multiple: false, required: false },
  vas_pp_certificate: { label: "PP Certificate", help: "Protection Plus certificate issued by the underwriter.", multiple: false, required: false },
  vas_pp_invoice: { label: "PP Invoice", help: "Tax invoice for the Protection Plus plan.", multiple: false, required: false },
  vas_amc_certificate: { label: "AMC Certificate", help: "Annual Maintenance Contract certificate.", multiple: false, required: false },
  vas_amc_invoice: { label: "AMC Invoice", help: "Tax invoice for the AMC plan.", multiple: false, required: false },
  vas_rsa_policy: { label: "RSA Policy", help: "Road-side assistance policy document.", multiple: false, required: false },
  form_20: { label: "Form 20", help: "RTO Form 20 — Application for registration of a motor vehicle.", multiple: false, required: false },
  form_22: { label: "Form 22", help: "RTO Form 22 — Roadworthiness certificate issued by the manufacturer.", multiple: false, required: false },
  other: { label: "Any Other Document", help: "Anything else that needs to be on file.", multiple: true, required: false },
  other_2: { label: "Any Other Document 2", help: "Second slot for any additional supporting document.", multiple: true, required: false },
};

export default function SaleUploads({ saleId, uploadedBy, types, onChange, readOnly = false }) {
  const TYPES = types || ["aadhar", "passport_photo", "pan", "vin_photo", "form_20", "other", "other_2"];
  const [filesByType, setFilesByType] = useState({});

  const refresh = async () => {
    if (!saleId) return;
    const all = await Files.list(saleId);
    const grouped = {};
    TYPES.forEach((t) => { grouped[t] = []; });
    all.forEach((f) => {
      if (!grouped[f.type]) grouped[f.type] = [];
      grouped[f.type].push(f);
    });
    setFilesByType(grouped);
    onChange?.(all);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [saleId]);

  return (
    <div className="space-y-4" data-testid="uploads-panel">
      {!saleId && (
        <div className="rounded-md border border-dashed border-border bg-slate-50 p-4 text-sm text-slate-600">
          Save the sale as a draft first to start uploading documents.
        </div>
      )}
      {TYPES.map((t) => (
        <UploadSlot
          key={t}
          type={t}
          meta={TYPE_META[t]}
          saleId={saleId}
          uploadedBy={uploadedBy}
          files={filesByType[t] || []}
          refresh={refresh}
          readOnly={readOnly}
        />
      ))}
    </div>
  );
}

function UploadSlot({ type, meta, saleId, uploadedBy, files, refresh, readOnly }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const policy = getUploadPolicy(type);

  const handleUpload = async (file) => {
    if (!file) return;
    if (!saleId) return toast.error("Save draft first");
    if (file.size > 10 * 1024 * 1024) return toast.error("File too large (max 10MB)");
    const err = validateUpload(type, file);
    if (err) {
      toast.error(err);
      return;
    }
    setBusy(true);
    try {
      await Files.upload(saleId, type, file, uploadedBy);
      toast.success(`${meta.label} uploaded`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this file?")) return;
    try {
      await Files.remove(id);
      toast.success("Removed");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const canAddMore = meta.multiple || files.length === 0;

  return (
    <div
      className="rounded-lg border border-border bg-white p-4"
      data-testid={`upload-slot-${type}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileImage className="h-4 w-4 text-primary" />
          <div className="text-sm font-semibold text-slate-900">
            {meta.label}
            {meta.required && <span className="ml-1 text-red-500">*</span>}
          </div>
          {meta.required && files.length === 0 && !readOnly && (
            <span className="ml-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red-700">
              Required
            </span>
          )}
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="rounded-full text-slate-400 hover:text-slate-600"
                  data-testid={`help-${type}`}
                >
                  <HelpCircle className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs text-xs">
                {meta.help}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {!readOnly && canAddMore && saleId && (
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept={policy.acceptAttr}
              className="hidden"
              onChange={(e) => handleUpload(e.target.files?.[0])}
              data-testid={`file-input-${type}`}
            />
            {policy.allowCamera && (
              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleUpload(e.target.files?.[0])}
                data-testid={`camera-input-${type}`}
              />
            )}
            {policy.allowCamera && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => cameraRef.current?.click()}
                disabled={busy}
                className="gap-1.5"
                data-testid={`capture-${type}`}
              >
                <Camera className="h-3.5 w-3.5" /> Take Photo
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="gap-1.5"
              data-testid={`upload-${type}`}
            >
              <Upload className="h-3.5 w-3.5" /> Upload Document
              <span className="text-[10px] font-normal opacity-80">({policy.acceptLabel})</span>
            </Button>
          </div>
        )}
      </div>

      {!readOnly && policy.helper && (
        <div className="mt-1 text-[11px] text-slate-500">{policy.helper}</div>
      )}

      <div className={cn("mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4", files.length === 0 && "hidden")}>
        {files.map((f) => (
          <FileTile key={f.id} file={f} policy={policy} onDelete={() => handleDelete(f.id)} readOnly={readOnly} />
        ))}
      </div>

      {files.length === 0 && (
        <div className="mt-3 flex items-center gap-2 rounded-md border border-dashed border-border bg-slate-50 px-3 py-3 text-xs text-slate-500">
          <Plus className="h-3.5 w-3.5" />
          {readOnly ? "No file uploaded" : (saleId ? "Click Upload to add a file" : "Save draft to enable uploads")}
        </div>
      )}
    </div>
  );
}

function FileTile({ file, policy, onDelete, readOnly }) {
  const isImage = file.content_type?.startsWith("image/");
  const previewUrl = Files.url(file.id);
  // When the type policy enables ``pdfOnDownload`` and the stored file is
  // actually an image (camera snap or JPG), force the download to come back
  // as a PDF.
  const downloadUrl = downloadUrlFor(file.type, file, previewUrl);
  const downloadLabel = policy?.pdfOnDownload && isImage ? "Download PDF" : "Download";
  const [uploaderName, setUploaderName] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!file.uploaded_by) return;
      try {
        const users = await Users.list();
        if (cancelled) return;
        const u = users.find((x) => x.id === file.uploaded_by);
        setUploaderName(u?.name || null);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [file.uploaded_by]);
  return (
    <div className="group relative overflow-hidden rounded-md border border-border bg-slate-50">
      {isImage ? (
        <a href={previewUrl} target="_blank" rel="noopener noreferrer">
          <img
            src={previewUrl}
            alt={file.original_filename}
            className="aspect-square w-full object-cover"
            data-testid={`file-preview-${file.id}`}
          />
        </a>
      ) : (
        <a
          href={previewUrl} target="_blank" rel="noopener noreferrer"
          className="flex aspect-square w-full flex-col items-center justify-center gap-1 p-3 text-center text-xs text-slate-600"
        >
          <FileImage className="h-7 w-7 text-slate-400" />
          <span className="truncate w-full">{file.original_filename}</span>
        </a>
      )}
      <div
        className="border-t border-border bg-white px-2 py-1 text-[10px] leading-tight text-slate-600"
        data-testid={`file-audit-${file.id}`}
      >
        <div className="truncate">
          {uploaderName ? (
            <span className="font-medium text-slate-700">{uploaderName}</span>
          ) : (
            <span className="italic text-slate-400">System</span>
          )}
        </div>
        <div className="font-mono-data text-slate-500">
          {formatAuditDate(file.uploaded_at)} {formatAuditTime(file.uploaded_at)}
        </div>
      </div>
      {!readOnly && (
        <button
          type="button"
          onClick={onDelete}
          className="absolute right-1 top-1 hidden rounded-md bg-white/95 p-1 text-red-600 shadow-sm hover:bg-red-50 group-hover:block"
          data-testid={`delete-file-${file.id}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
      {policy?.pdfOnDownload && isImage && (
        <a
          href={downloadUrl}
          className="absolute left-1 top-1 hidden items-center gap-0.5 rounded-md bg-white/95 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700 shadow-sm hover:bg-slate-50 group-hover:inline-flex"
          title={downloadLabel}
          data-testid={`download-pdf-${file.id}`}
        >
          <Download className="h-3 w-3" /> PDF
        </a>
      )}
    </div>
  );
}
